// eventEmitter — buffered POST of cheating events to the backend.
//
// Detection modules emit events at high frequency (a state machine may
// fire 10+ times per minute for an attentive candidate, 100+ times for a
// noisy one). Posting per-event is wasteful and hammers the backend.
// This module buffers events for up to FLUSH_INTERVAL_MS, then POSTs in
// one batch. Flushes immediately for `severity=critical` events so the
// recruiter dashboard sees them with low latency.
//
// Guarantees:
//   * No event lost on page unload (flushes via `navigator.sendBeacon`).
//   * No backend pile-up (batching).
//   * Critical events flushed immediately (low latency for serious flags).
//   * Backpressure: if the buffer grows beyond MAX_BUFFER, oldest events
//     are dropped (better to lose old INFO events than crash).
//
// Public API:
//   createEventEmitter({ testId, applicationId, candidateEmail })
//     → { emit(event), flush(), shutdown() }

import axios from 'axios';

const FLUSH_INTERVAL_MS = 5000;   // batch every 5s in steady state
const MAX_BUFFER = 200;           // drop oldest if we exceed this
const FLUSH_ON_SHUTDOWN_TIMEOUT_MS = 1500;

const BULK_ENDPOINT = '/api/cheating/events/bulk';
const SINGLE_ENDPOINT = '/api/cheating/event';

export function createEventEmitter({
  testId,
  applicationId = null,
  candidateEmail = null,
} = {}) {
  if (!testId) {
    // eslint-disable-next-line no-console
    console.error('[proctoring/emit] missing testId — events will be dropped');
  }

  let buffer = [];
  let timer = null;
  let shutdown = false;

  // ── Internal: actually POST a batch ────────────────────────────────────
  const sendBatch = async (events) => {
    if (!events || events.length === 0) return;
    try {
      await axios.post(BULK_ENDPOINT, { events }, { timeout: 8000 });
    } catch (err) {
      // Don't retry — drop and move on. Detection is a continuous stream;
      // missing one batch is better than building up a queue that would
      // hammer the backend on every retry.
      // eslint-disable-next-line no-console
      console.warn('[proctoring/emit] batch POST failed:', err?.message || err);
    }
  };

  // ── Critical events flush immediately via the single endpoint so the
  //    recruiter dashboard sees them with the lowest possible latency.
  const sendOneImmediate = async (ev) => {
    try {
      await axios.post(SINGLE_ENDPOINT, ev, { timeout: 8000 });
    } catch (err) {
      // Last-ditch: re-queue at the back of the buffer so the next batch
      // sweeps it up. Better than silently losing a critical event.
      // eslint-disable-next-line no-console
      console.warn('[proctoring/emit] critical event POST failed, re-queueing:',
                   err?.message || err);
      buffer.push(ev);
      trim();
    }
  };

  // ── Drop oldest events when buffer overflows ───────────────────────────
  const trim = () => {
    if (buffer.length > MAX_BUFFER) {
      const overflow = buffer.length - MAX_BUFFER;
      buffer.splice(0, overflow);
    }
  };

  const flush = async () => {
    if (buffer.length === 0) return;
    const toSend = buffer;
    buffer = [];
    await sendBatch(toSend);
  };

  const startTimer = () => {
    if (timer || shutdown) return;
    timer = setInterval(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);
  };

  const stopTimer = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  startTimer();

  // ── Public: emit a single event. Returns void; do not await. ────────────
  const emit = (event) => {
    if (shutdown) return;
    if (!event || !event.feature_key) return;

    const enriched = {
      test_id: testId,
      application_id: applicationId,
      candidate_email: candidateEmail,
      severity: event.severity || 'info',
      score_delta: event.score_delta || 0,
      message: event.message || '',
      evidence: event.evidence || {},
      ...event, // allow per-event overrides AFTER the defaults
      // …but lock test_id back so the caller can never accidentally spoof:
      test_id: testId,
    };

    if (enriched.severity === 'critical') {
      // Don't wait for the batch — fire now.
      void sendOneImmediate(enriched);
      return;
    }

    buffer.push(enriched);
    trim();
  };

  // ── Best-effort flush on page unload. sendBeacon doesn't return a
  //    Promise but the browser guarantees delivery even after navigation. ─
  const unloadHandler = () => {
    if (buffer.length === 0) return;
    try {
      const payload = JSON.stringify({ events: buffer });
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(BULK_ENDPOINT, blob);
      buffer = [];
    } catch (_) { /* defensive */ }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', unloadHandler);
    window.addEventListener('pagehide', unloadHandler);
  }

  // ── Public: stop the timer + flush remaining events. ────────────────────
  const stop = async () => {
    shutdown = true;
    stopTimer();
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', unloadHandler);
      window.removeEventListener('pagehide', unloadHandler);
    }
    // Final flush with timeout cap.
    await Promise.race([
      flush(),
      new Promise((r) => setTimeout(r, FLUSH_ON_SHUTDOWN_TIMEOUT_MS)),
    ]);
  };

  return { emit, flush, stop };
}
